/**
 * NewUserModal Component - Modal for creating new users
 */
import React, { useState } from 'react';
import { mascaraTelefone } from '../../lib/utils';
import { ROLE_OPTIONS } from '../../constants/roles';
import SMTPConfigSection from '../SMTPConfigSection';
import { NewUserData, getInitialNewUser } from '../../types/user';
import useAuthStore from '../../stores/authStore';

interface NewUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (user: NewUserData) => Promise<boolean>;
}

export const NewUserModal: React.FC<NewUserModalProps> = ({
  isOpen,
  onClose,
  onCreate,
}) => {
  const [newUser, setNewUser] = useState<NewUserData>(getInitialNewUser());
  const [creating, setCreating] = useState(false);
  // N116: lê do store em vez de receber por prop — é como o UsersPage já faz
  // (useAuthStore((s) => s.user)) e evita prop drilling só para um checkbox.
  // A visibilidade aqui é conveniência; quem garante é o backend, que exige
  // admin no endpoint de usuários.
  const isMaster = useAuthStore((s) => s.user)?.role === 'MASTER';

  const handleCreate = async () => {
    setCreating(true);
    const success = await onCreate(newUser);
    setCreating(false);

    if (success) {
      setNewUser(getInitialNewUser());
      onClose();
    }
  };

  const handleClose = () => {
    setNewUser(getInitialNewUser());
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-8 max-w-[800px] w-[90%] max-h-[90vh] overflow-y-auto shadow-2xl">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Criar Novo Usuário
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
          Preencha os dados do novo usuário abaixo
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column: User Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b pb-2">
              Dados Pessoais
            </h3>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                  Username *
                </label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  placeholder="Digite o username"
                  className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  placeholder="Ex: João da Silva"
                  className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                  E-mail *
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="Digite o e-mail"
                  className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                  Telefone
                </label>
                <input
                  type="tel"
                  value={newUser.telefone}
                  onChange={(e) => setNewUser({ ...newUser, telefone: mascaraTelefone(e.target.value) })}
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                  className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                  Função no Sistema *
                </label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {ROLE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  id="new-user-email-notif"
                  checked={newUser.receber_email}
                  onChange={(e) => setNewUser({ ...newUser, receber_email: e.target.checked })}
                  className="w-5 h-5 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                />
                <label htmlFor="new-user-email-notif" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Receber notificações por e-mail
                </label>
              </div>

              {/* N116 (Alana): só MASTER vê e altera. O rótulo diz a
                  CONSEQUÊNCIA, não o mecanismo, porque este checkbox convive na
                  mesma tela com o "Receber notificações por e-mail" acima — e os
                  dois são coisas diferentes: aquele é preferência do usuário,
                  este coloca o e-mail nos envios que vão para hospital e
                  cliente. */}
              {/* ⛔ N166: o gate era só `isMaster`, que fala do usuário LOGADO
                  (useAuthStore) — não do usuário sendo cadastrado. Resultado: um
                  MASTER criando um auditor via o checkbox, e 13 AUDITORES
                  acabaram marcados. Agora depende do PERFIL ESCOLHIDO. A trava
                  de verdade é no backend (user_service/user_creation_service):
                  esconder na tela nunca protegeu nada. */}
              {isMaster && newUser.role === 'MASTER' && (
                <div className="pt-2">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="new-user-email-offline"
                      checked={newUser.incluir_email_offline}
                      onChange={(e) => setNewUser({ ...newUser, incluir_email_offline: e.target.checked })}
                      className="w-5 h-5 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <label htmlFor="new-user-email-offline" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Sempre incluir este e-mail nos envios do app offline
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-8">
                    O e-mail passa a aparecer já marcado na lista de
                    destinatários do aplicativo, junto dos contatos do hospital.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Password & SMTP */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b pb-2 mb-4">
                Segurança
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                    Senha *
                  </label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="Mínimo 6 caracteres"
                    className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                    Confirmar Senha *
                  </label>
                  <input
                    type="password"
                    value={newUser.confirmPassword}
                    onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })}
                    placeholder="Digite a senha novamente"
                    className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </div>
              </div>
            </div>

            <div>
              <SMTPConfigSection
                config={{
                  smtp_email: newUser.smtp_email,
                  smtp_senha: newUser.smtp_senha,
                  smtp_servidor: newUser.smtp_servidor,
                  smtp_porta: newUser.smtp_porta,
                  smtp_tls: newUser.smtp_tls,
                }}
                onChange={(config) => setNewUser({
                  ...newUser,
                  smtp_email: config.smtp_email,
                  smtp_senha: config.smtp_senha,
                  smtp_servidor: config.smtp_servidor,
                  smtp_porta: config.smtp_porta,
                  smtp_tls: config.smtp_tls,
                })}
                hasPassword={false}
                collapsible={true}
                defaultExpanded={true}
                showTestButton={false}
                title="Configuração SMTP (Opcional)"
                compact={true}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-8 justify-end">
          <button
            onClick={handleClose}
            className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-semibold transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
          >
            {creating ? 'Criando...' : 'Criar Usuário'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewUserModal;
